import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdContentPaste,
  MdLocalShipping,
  MdMap,
  MdPerson,
  MdWarning,
} from "react-icons/md";
import { PageTitle } from "../../components/PageTitle";
import { CanAccess } from "../../components/CanAccess";

interface ActionButtonProps {
  title: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  color: string;
  onClick: () => void;
}

function ActionButton({ title, icon: Icon, color, onClick }: ActionButtonProps) {
  // Mapear cores para gradientes e estilos
  const colorMap: Record<string, { gradient: string; shadow: string; hoverGradient: string }> = {
    blue: {
      gradient: "from-blue-500 to-blue-600",
      shadow: "shadow-blue-500/30",
      hoverGradient: "hover:from-blue-600 hover:to-blue-700",
    },
    green: {
      gradient: "from-green-500 to-emerald-500",
      shadow: "shadow-green-500/30",
      hoverGradient: "hover:from-green-600 hover:to-emerald-600",
    },
    orange: {
      gradient: "from-orange-500 to-amber-500",
      shadow: "shadow-orange-500/30",
      hoverGradient: "hover:from-orange-600 hover:to-amber-600",
    },
    purple: {
      gradient: "from-purple-500 to-indigo-500",
      shadow: "shadow-purple-500/30",
      hoverGradient: "hover:from-purple-600 hover:to-indigo-600",
    },
    red: {
      gradient: "from-red-500 to-red-600",
      shadow: "shadow-red-500/30",
      hoverGradient: "hover:from-red-600 hover:to-red-700",
    },
  };

  const colors = colorMap[color] || colorMap.blue;

  return (
    <button
      onClick={onClick}
      className={`
        group relative overflow-hidden
        bg-gradient-to-br ${colors.gradient}
        ${colors.hoverGradient}
        ${colors.shadow} hover:shadow-xl
        rounded-2xl p-6
        transition-all duration-300
        transform hover:scale-105 active:scale-95
        flex flex-col items-center justify-center gap-3
        min-h-[160px]
      `}
    >
      <div className="bg-white/20 p-4 rounded-full group-hover:bg-white/30 transition-colors">
        <Icon size={32} className="text-white" />
      </div>
      <h3 className="text-lg font-bold text-white text-center">
        {title}
      </h3>
      <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity" />
    </button>
  );
}

export function Home() {
  const navigate = useNavigate();

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageTitle title="Início" />

      <div className="mt-8 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Bem-vindo ao Sistema Luvilimp
        </h2>
        <p className="text-gray-600">
          Escolha uma ação abaixo para começar:
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 px-2">
        <CanAccess permission={["tech-visit-reports:create-visit", "tech-visit-reports:create"]} allowAdmin>
          <ActionButton
            title="Visita Técnica"
            icon={MdContentPaste}
            color="blue"
            onClick={() => navigate("/tech-visit-reports/new")}
          />
        </CanAccess>

        <CanAccess permission={["tech-visit-reports:create-delivery", "tech-visit-reports:create"]} allowAdmin>
          <ActionButton
            title="Entrega"
            icon={MdLocalShipping}
            color="green"
            onClick={() => navigate("/delivery-reports/new")}
          />
        </CanAccess>

        <CanAccess permission="costumers-map:view" allowAdmin>
          <ActionButton
            title="Mapa"
            icon={MdMap}
            color="orange"
            onClick={() => navigate("/costumers/map")}
          />
        </CanAccess>

        <CanAccess permission="responsibles:read" allowAdmin>
          <ActionButton
            title="Responsáveis"
            icon={MdPerson}
            color="purple"
            onClick={() => navigate("/responsibles")}
          />
        </CanAccess>

        <CanAccess permission="costumer-observations:create" allowAdmin>
          <ActionButton
            title="Observação Emergencial"
            icon={MdWarning}
            color="red"
            onClick={() => navigate("/emergency-observations")}
          />
        </CanAccess>
      </div>

      <div className="mt-10 p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Acesso Rápido
        </h3>
        <p className="text-gray-600 text-sm">
          Use os botões acima para acessar rapidamente as funcionalidades mais utilizadas do sistema.
          Para acessar outras áreas, utilize o menu lateral.
        </p>
      </div>
    </div>
  );
}
